<template>
    <div class="flex flex-col overflow-hidden w-100 h-full">
        <!-- Channel header -->
        <div id="channel-header-container">
            <div class="text-center bg-gray-800">
                <div class="flex items-center justify-between mr-2 font-semibold text-white outline-none py-2 pl-2">
                    <span class="mr-2 cursor-pointer flex items-center">
                        <!-- Mute Channel Icon -->
                        <svg v-if="!channel.auth_user_settings.has_muted_channel" @click.prevent="toggleMuteChannel"
                             class="w-5 h-5 text-indigo-400 mr-1" viewBox="0 0 24 24">
                            <path fill="currentColor"
                                  d="M14,3.23V5.29C16.89,6.15 19,8.83 19,12C19,15.17 16.89,17.84 14,18.7V20.77C18,19.86 21,16.28 21,12C21,7.72 18,4.14 14,3.23M16.5,12C16.5,10.23 15.5,8.71 14,7.97V16C15.5,15.29 16.5,13.76 16.5,12M3,9V15H7L12,20V4L7,9H3Z"/>
                        </svg>
                        <!-- Unmute Channel Icon -->
                        <svg v-else class="w-5 h-5 text-gray-400 mr-1" viewBox="0 0 24 24"
                             @click.prevent="toggleMuteChannel">
                            <path fill="currentColor"
                                  d="M3,9H7L12,4V20L7,15H3V9M16.59,12L14,9.41L15.41,8L18,10.59L20.59,8L22,9.41L19.41,12L22,14.59L20.59,16L18,13.41L15.41,16L14,14.59L16.59,12Z"/>
                        </svg>

                        <span>{{ channel.name }}</span>
                    </span>

                    <div class="flex">
                        <!-- Search Button (Remove for version 1. Can add back later as new feature) -->
                        <!--<svg @click.prevent="toggleSearchMode" class="cursor-pointer mr-4 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                        </svg>-->

                        <!-- Settings Menu Button -->
                        <div class="mr-1 relative" v-click-outside="hideSettingsMenu">
                            <!-- Settings Menu dots icon -->
                            <svg @click.prevent="toggleSettingsMenuDropdown"
                                 class="cursor-pointer w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24"
                                 stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"/>
                            </svg>
                            <!-- Actual settings dropdown -->
                            <transition
                                enter-active-class="transition ease-out duration-100"
                                enter-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95">
                                <div v-show="showSettingsMenuDropdown"
                                     class="absolute text-right z-10 top-0 right-0 bg-gray-700 rounded-md p-3 mt-7 -mr-1 shadow-sm text-sm text-white font-medium">
                                    <div
                                        class="absolute z-10 top-0 bg-gray-700 w-3 h-3 -mt-1 transform rotate-45 mr-5 rounded-tl-sm"
                                        style="right: -12px"></div>
                                    <button @click.prevent="toggleMuteChannel"
                                            class="p-2 text-right hover:text-indigo-400 hover:bg-gray-600 rounded-md outline-none">
                                        {{ channel.auth_user_settings.has_muted_channel ? "Unmute" : "Mute" }}
                                    </button>
                                    <button @click.prevent="toggleSubscription"
                                            class="p-2 text-right  hover:text-indigo-400 hover:bg-gray-600 rounded-md outline-none">
                                        {{ channel.auth_user_settings.is_subscribed ? "Unsubscribe" : "Subscribe" }}
                                    </button>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>
                <transition
                    enter-active-class="transition ease-out duration-100"
                    enter-class="transform opacity-0 scale-95"
                    enter-to-class="transform opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-75"
                    leave-class="transform opacity-100 scale-100"
                    leave-to-class="transform opacity-0 scale-95">
                    <div v-show="showMessageSearch" class="px-2">
                        <input v-model="searchQuery" @keyup="searchChannel" id="searchInput"
                               type="text"
                               class="bg-gray-800 text-gray-300 w-full my-4 border-b border-gray-400 outline-none"
                               placeholder="Search"/>
                    </div>
                </transition>
            </div>
        </div>

        <!-- Channel Messages -->
        <div ref="containerMessageDisplay" id="message-container"
             class="flex flex-col flex-1 overflow-y-scroll overflow-x-hidden scrollbar-thin"
             style="max-height:100%"
             @scroll="updateScrollState">
            <div v-show="loadingMessages" class="px-2 text-center my-10 font-bold">
                Loading...
            </div>
            <div v-show="messages.length === 0 && !loadingMessages" class="px-2 text-center my-10 font-bold">
                No Messages Found!
            </div>
            <div style="min-height: 102%">
                <div v-show="messages.length > 0" v-for="message in messages" class="flex flex-col flex-wrap px-2">
                    <text-message v-if="message.type === 'text'" :message="message" :key="message.id"
                                  @delete-message="deleteMessage"
                                  @scroll-to-reply="scrollToReply">
                    </text-message>
                    <media-message v-if="message.type === 'media'" :message="message" :key="message.id"
                                   @delete-message="deleteMessage"
                                   @scroll-to-reply="scrollToReply">
                    </media-message>
                    <system-message v-if="message.type === 'system'" :message="message"
                                    :key="message.id"></system-message>
                </div>
            </div>
        </div>

        <!-- Channel footer/message input component -->
        <div id="message-input-container">
            <message-input id="message-input"
                           :channel="channel"
                           :show-attachments-upload-button="showAttachmentsUploadButton"
                           :show-share-existing-media-button="showShareExistingMediaButton"
                           @message-sent="addMessage($event,false)">
            </message-input>
        </div>
    </div>
</template>

<script>
import "../../../css/messaging.css";
import VueScrollTo from "vue-scrollto";
import dayjs from "dayjs";

export default {
    name: "MessengerChannel",

    components: {
        VueScrollTo,
        dayjs,
    },

    props: {
        echo: {
            type: Object,
            required: true
        },
        channel: {
            type: Object,
            required: true,
        },
        isFullPage: {
            type: Boolean,
            default: false,
            required: false
        },
        panelHeight: {
            type: String,
            required: false,
            default: "60vh"
        },
        showAttachmentsUploadButton: {
            type: Boolean,
            required: false,
            default: true
        },
        showShareExistingMediaButton: {
            type: Boolean,
            required: false,
            default: true
        }
    },

    created () {
        let self = this;

        self.getMessages();
        self.updateLastSeen();

        //overflow should always be removed to prevent background scrolling
        document.body.style.setProperty("overflow", "hidden");
    },
    mounted () {
        let self = this;
        self.messageChannel = this.echo.private("messaging.channel." + self.channel.id);
        self.messageChannel
        .listen("MessageSent", (event) => {
            self.updateLastSeen();
            self.addMessage(event.message, true);
        })
        .listen("MessageDeleted", (event) => {
            self.updateLastSeen();
            self.modifyDeletedMessage(event.message);
        })
        .listen("MessageReadByAll", (event) => {
            self.updateReadReceipts(event.messageArray);
        });

        self.userPresenceChannel = this.echo.join("online-channel." + self.channel.id);

        self.userPresenceChannel
        .here(users => {
            self.users = users;
            users.forEach((user) => {
                self.updateUserPresence(user);
            });
        })
        .joining(user => {
            self.updateUserPresence(user);
            self.updateLastSeen();
        })
        .leaving(user => {
            self.updateUserPresence(user, false);
        });
    },
    beforeDestroy () {
        this.updateUserPresence(this.state.user, false);

        this.echo.leaveChannel(this.userPresenceChannel.name);
        this.echo.leaveChannel(this.messageChannel.name);

        // Do not show your last message as an unread message
        this.updateLastSeen();

        //reset scrolling
        document.body.style.setProperty("overflow", "scroll");
    },

    data () {
        return {
            users: [],
            messages: [],
            loadingMessages: true,
            lastReceivedMessageId: 0,
            searchQuery: "",
            showMessageSearch: false,
            showSettingsMenuDropdown: false,
            userPresenceChannel: null,
            messageChannel: null,
            allMessagesLoaded: false,
        };
    },

    computed: {
        screenHeight () {
            return Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
        },

        screenPixelRatio () {
            return window.devicePixelRatio === 1 ? window.devicePixelRatio : (window.devicePixelRatio * 0.9).toFixed(2);
        },
    },

    methods: {
        updateScrollState ({target: {scrollTop, clientHeight, scrollHeight}}) {
            this.updateScroll = scrollTop + clientHeight >= scrollHeight;
            if (scrollTop < 10) {
                this.infiniteHandler();
            }
        },

        goToBottom () {
            let scrollDiv = this.$refs.containerMessageDisplay;
            scrollDiv.scrollTop = scrollDiv.scrollHeight;
            this.updateScroll = false;
        },

        scrollToLocation (messageId = null) {
            if (messageId) {
                // Scroll to element
                let elem = document.getElementById("message-container");
                if (elem !== null) {
                    elem.scrollTop = document.getElementById("message-" + (messageId)).offsetTop - 126 * this.screenPixelRatio;
                }
            } else {
                // Scroll to bottom
                this.goToBottom();
            }
        },

        getMessages () {
            this.loadingMessages = true;

            axios.get("/messages/" + this.channel.id)
            .then((response) => {

                let isFirstPage = this.messages.length <= 0;

                this.messages = response.data;
                this.lastReceivedMessageId = response.data[0].id;

                if (isFirstPage) {
                    this.$nextTick(function () {
                        this.goToBottom();
                    });
                }
                this.loadingMessages = false;
            })
            .catch((error) => {
                toastr.error("Error Loading Messages! " + error.message);
            });
        },

        addMessage (message, fromPusher = false) {
            if (fromPusher) {
                message.is_message_by_authenticated_user = false;
            }

            if (this.messages.findIndex(m => m.id === message.id) < 0) {
                this.messages.push(message);
                this.$nextTick(function () {
                    this.goToBottom();
                });
            }
        },

        modifyDeletedMessage (message) {
            let item = this.messages.find(item => item.id === message.id);
            let index = this.messages.indexOf(item);

            if (index > -1) {
                this.messages[index].message = "deleted";
                this.messages[index].media = [];
                this.messages[index].replied_message = null;
            }
        },

        deleteMessage (message) {
            if (dayjs(message.sent_at).add(10, "minute").isBefore(dayjs())) {
                toastr.error("You cannot delete messages that are older than 10 minutes");
                return;
            }

            App.post("/messages/delete/" + message.id, new AppForm({}))
            .then(() => {
                let index = this.messages.indexOf(message);
                if (index > -1) {
                    this.messages.splice(index, 1);

                    // The message we want to send along with this event is the last message
                    // of the channel after the delete happened as that will be set as the new
                    // last_message on the channel list item as well.
                    Bus.$emit("update-own-list", this.messages[this.messages.length - 1]);
                    toastr.success("Message successfully deleted");
                } else {
                    toastr.error("Message not found!");
                }
            })
            .catch((error) => {
                toastr.error("Unable to delete message! Error: " + error);
            });
        },

        infiniteHandler () {
            if (this.loadingMessages || this.allMessagesLoaded) {
                return false;
            }
            this.loadingMessages = true;

            axios.get("/messages/" + this.channel.id + "/" + this.lastReceivedMessageId)
            .then((response) => {
                if (response.data.length > 0) {
                    this.lastReceivedMessageId = response.data[0].id;
                    this.messages = response.data.concat(this.messages);
                } else {
                    this.allMessagesLoaded = true;
                }
            })
            .then(() => {
                this.loadingMessages = false;
            })
            .catch((error) => {
                toastr.error("Error Loading Messages! Error:" + error.message);
            });
        },

        toggleMuteChannel () {
            this.channel.auth_user_settings.has_muted_channel = !this.channel.auth_user_settings.has_muted_channel;

            axios.post("/messenger/channels/mute/" + this.channel.id)
            .then((response) => {
                toastr.success(response.data);
            })
            .catch((error) => {
                toastr.error("Unable to mute channel! " + error.message);
                this.channel.auth_user_settings.has_muted_channel = !this.channel.auth_user_settings.has_muted_channel;
            });
        },

        updateLastSeen () {
            if (!this.showMessageSearch && !this.loadingMessages) {
                axios.post("/messenger/channels/update-last-seen/" + this.channel.id, new AppForm({
                        users: Object.values(this.users).map(user => user.id)
                    }
                ));
            }
        },

        updateReadReceipts (unreadMessages) {
            let readMessages = unreadMessages ? this.messages.filter((message) => !unreadMessages.includes(message.id)) : this.messages;

            readMessages.forEach((message) => {
                let item = this.messages.find(item => item.id === message.id);
                let index = this.messages.indexOf(item);
                this.messages[index].is_read_by_all = true;
            });
        },

        searchChannel: _.debounce(function () {
            this.loadingMessages = true;

            axios.get("/messages/search", {
                params: {
                    filter: this.searchQuery,
                    messenger_channel_id: this.channel.id
                }
            })
            .then((response) => {
                this.messages = response.data["messages"];
                let bestMatchId = response.data["bestMatchId"];

                this.$nextTick(function () {
                    if (bestMatchId) {
                        this.scrollToLocation(bestMatchId);
                    }
                });
                this.loadingMessages = false;
            })
            .catch((error) => {
                toastr.error("Error While Searching Messages! " + error.message);
            });
        }, 400),

        updateUserPresence (user, userOnPage = true) {
            Object.assign({}, this.user, this.userPresenceChannel.subscription.members.members);

            axios.post("/messenger/channels/update-user-presence/" + this.channel.id, new AppForm({
                user_id: user.id,
                presence_status: userOnPage
            }));
        },

        toggleSettingsMenuDropdown () {
            this.showSettingsMenuDropdown = !this.showSettingsMenuDropdown;
        },

        hideSettingsMenu () {
            this.showSettingsMenuDropdown = false;
        },

        toggleSubscription () {
            this.channel.auth_user_settings.is_subscribed = !this.channel.auth_user_settings.is_subscribed;

            axios.post("/messenger/channels/toggle-subscription/" + this.channel.id)
            .then((response) => {
                toastr.success(response.data);
            })
            .catch((error) => {
                this.channel.auth_user_settings.is_subscribed = !this.channel.auth_user_settings.is_subscribed;
                toastr.error("Unable to leave channel! " + error.message);
            });
        },

        // Finds message if it is loaded, else searches for the message
        scrollToReply (message) {
            let item = this.messages.find(m => m.id === message.id);
            if (item) {
                this.scrollToLocation(item.id);
            } else {
                this.searchQuery = message.message;
                this.searchChannel();
            }
        },
    }
};
</script>

<style>

</style>
